import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehicleUnitMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { copy } from "angular";
import { lastValueFrom, Observable, Subject } from "rxjs";
import { tap } from "rxjs/operators";
import { DataCacheService } from "./caching/data-cache.service";
import { EnumService } from "./enum.service";
import { TranslationService } from "./translation.service";

export enum DeviceParameterType {
    OverRev,
    GreenBandLower,
    GreenBandUpper,
    SweetSpotLower,
    SweetSpotUpper
}

export interface IDeviceParameterProfileQueryOptions {
    MaterialisationOptions?: DeviceParameterProfileMaterialisationOptions;
    Id?: number;
    AccountId?: number;
    Active?: boolean;
}

export interface ILatestDeviceParameterProfileQueryOptions {
    Serial?: string;
    DeviceParameterRecordId?: number;
}

export enum DeviceParameterProfileMaterialisationOptions {
    All = -1,
    Basic = 0,
    WithMappings = 1
}

export interface IDeviceParameterProfile {
    Id: number;
    AccountId?: number;
    Name: string;
    ParameterMap: DeviceParameterMap;
    Active: boolean;
    CreatedTime?: Date;
    UpdatedTime?: Date;
    UnitDeviceParameterProfileMappings: IDeviceParameterProfileMapping[];

    // ---

    parametersSetLabel?: string;
    appliesToLabel?: string;
}

export interface IDeviceParameterRecord {
    Id: number;
    Serial: string;
    PlotId: number;
    EventTime: Date;
    ParameterMap: DeviceParameterMap;
    CreatedTime: Date;
}

export interface ILatestDeviceParameterRecordQueryOptions {
    Serial?: string;
    DeviceParameterRecordId?: number;
}

export interface IDeviceParameterProfileMapping {
    Id: number;
    VehicleId: number;
    DeviceParameterProfileId: number;
    CreatedTime?: Date;
    UpdatedTime?: Date;
    DeviceParameterProfile?: IDeviceParameterProfile;

    // ---

    validationError?: string;
}

export interface IDeviceParameterComparisonResult {
    Comparisons: IDeviceParameterComparison[];
    IsCorrect: boolean;

    // ---

    vehicleHasUnit?: boolean;
    hasAssignedProfile?: boolean;
}

export interface IDeviceParameterComparison {
    DeviceParameterType: DeviceParameterType;
    ProfileValue?: number;
    DeviceValue?: number;
    DeviceSupported: boolean;
    IsCorrect: boolean;
}

export enum DeviceComparisonStatus {
    NoProfileAssigned,
    NoUnitInstalled,
    IncompatibleUnit,
    Synchronized,
    AwaitingSync,
}

export type DeviceParameterComparisonResultMap = { [key: number]: IDeviceParameterComparisonResult }; // key: VehicleId
export type DeviceParameterMap = { [key: number]: number }; // key: DeviceParameterType

@Injectable({
    providedIn: 'root'
})
export class DeviceProfileService {
    readonly deviceParameterDefaults = {
        [DeviceParameterType.GreenBandLower]: 600,
        [DeviceParameterType.SweetSpotLower]: 1000,
        [DeviceParameterType.SweetSpotUpper]: 1400,
        [DeviceParameterType.GreenBandUpper]: 1800,
        [DeviceParameterType.OverRev]: 2200,
    };

    private deviceProfilesSubject: Subject<IDeviceParameterProfile[]> = new Subject<IDeviceParameterProfile[]>();
    deviceProfilesUpdated = this.deviceProfilesSubject.asObservable();

    private profilesCache: string = 'activeDeviceProfilesCache';

    constructor(
        private readonly httpClient: HttpClient,
        private readonly dataCacheService: DataCacheService,
        private readonly translationService: TranslationService
    ) { }

    getComparisonsForAllVehicles(invalidateCache: boolean = false): Promise<DeviceParameterComparisonResultMap> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<DeviceParameterComparisonResultMap>(`/api/deviceparameters/comparisons`, options));
    }

    getComparisonForVehicle(vehicle: IVehicle, invalidateCache: boolean = false): Promise<IDeviceParameterComparisonResult> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDeviceParameterComparisonResult>(`/api/deviceparameters/comparison/${vehicle.Id}`, options)).then(comparison => {

            if (!comparison) {
                return {
                    Comparisons: [],
                    IsCorrect: false,
                    vehicleHasUnit: vehicle.HasUnit
                }
            }

            comparison.vehicleHasUnit = vehicle.HasUnit;

            return comparison;
        });
    }

    getProfiles(profileQueryOptions: IDeviceParameterProfileQueryOptions = {}, invalidateCache: boolean = false): Observable<IDeviceParameterProfile[]> {
        if (profileQueryOptions.Id || profileQueryOptions.AccountId) {
            return this.getProfilesFromDb(profileQueryOptions);
        }

        return this.dataCacheService.getOrRequest(this.profilesCache, () => this.getProfilesFromDb(profileQueryOptions), { invalidateCache });
    }

    getProfilesFromDb(profileQueryOptions: IDeviceParameterProfileQueryOptions = {}): Observable<IDeviceParameterProfile[]> {
        const options = { params: profileQueryOptions as HttpParams }

        return this.httpClient.get<IDeviceParameterProfile[]>(`/api/deviceparameters/profiles`, options);
    }

    getProfileForVehicle(vehicleId: number, invalidateCache: boolean = false): Promise<IDeviceParameterProfile> {
        const options = { params: new HttpParams() };

        options.params.set("minsToLive", 30);
        options.params.set("invalidateCache", invalidateCache);

        return lastValueFrom(this.httpClient.get<IDeviceParameterProfile>(`/api/deviceparameters/profile/${vehicleId}`, options));
    }

    getDeviceParameterRecords(profileQueryOptions: IDeviceParameterProfileQueryOptions = {}, invalidateCache: boolean = false): Promise<IDeviceParameterRecord[]> {
        const options = { params: profileQueryOptions as HttpParams };

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDeviceParameterRecord[]>(`/api/deviceparameters/records`, options));
    }

    getLatestDeviceParameterRecords(profileQueryOptions: ILatestDeviceParameterProfileQueryOptions = {}, invalidateCache: boolean = false): Promise<IDeviceParameterRecord[]> {
        const options = { params: profileQueryOptions as HttpParams };

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDeviceParameterRecord[]>(`/api/deviceparameters/records/latest`, options));
    }

    getLatestRecords(latestParameterRecordQueryOptions: ILatestDeviceParameterRecordQueryOptions = {}, invalidateCache: boolean = false): Promise<IDeviceParameterRecord[]> {
        const options = { params: latestParameterRecordQueryOptions as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDeviceParameterRecord[]>(`/api/deviceparameters/profile/latest`, options));
    }

    addUpdateProfile(deviceParameterProfile: IDeviceParameterProfile, invalidateCache: boolean = false): Promise<IDeviceParameterProfile> {
        const options = { params: new HttpParams() };

        options.params.set("minsToLive", 30);
        options.params.set("invalidateCache", invalidateCache);

        return lastValueFrom(this.httpClient.post<IDeviceParameterProfile>(`/api/deviceparameters/profile`, deviceParameterProfile, options).pipe(
            tap((profile: IDeviceParameterProfile) => {
                if (!deviceParameterProfile.CreatedTime) {
                    profile.CreatedTime = profile.UpdatedTime = new Date();
                }
                this.dataCacheService.addOrUpdateItem(profile, this.profilesCache);
            })
        ));
    }

    deleteProfile(deviceParameterProfile: IDeviceParameterProfile, invalidateCache: boolean = false): Promise<IDeviceParameterProfile> {
        const deleteModel = copy(deviceParameterProfile);

        deleteModel.Active = false;
        deleteModel.UnitDeviceParameterProfileMappings = [];

        return this.addUpdateProfile(deleteModel, invalidateCache);
    }

    addMapping(deviceProfileMapping: IDeviceParameterProfileMapping, invalidateCache: boolean = false): Promise<IDeviceParameterProfileMapping> {
        const options = { params: new HttpParams() };

        options.params.set("minsToLive", 30);
        options.params.set("invalidateCache", invalidateCache);

        return lastValueFrom(this.httpClient.post<IDeviceParameterProfileMapping>(`/api/deviceparameters/mapping`, deviceProfileMapping, options));
    }

    deleteMapping(deviceProfileMapping: IDeviceParameterProfileMapping, invalidateCache: boolean = false): Promise<any> {
        const options = {
            params: new HttpParams(),
            body: deviceProfileMapping
        };

        options.params.set("minsToLive", 30);
        options.params.set("invalidateCache", invalidateCache);

        return lastValueFrom(this.httpClient.delete<any>(`/api/deviceparameters/mapping`, options));
    }

    createDeviceParameterProfileTemplate(): IDeviceParameterProfile {
        const newProfileTemplate: IDeviceParameterProfile = {
            Id: 0,
            Name: "",
            ParameterMap: this.createDeviceParameterMap(),
            Active: true,
            UnitDeviceParameterProfileMappings: []
        }

        return newProfileTemplate;
    }

    createDeviceParameterMap(): DeviceParameterMap {
        const deviceParameterTypes: IKeyValuePair<typeof DeviceParameterType>[] = EnumService.enumToArray("DeviceParameterType", DeviceParameterType);
        deviceParameterTypes.sort((deviceParameterType1, deviceParameter2) => (deviceParameterType1.value > deviceParameter2.value) ? 1 : -1);

        const newDeviceParameterMap: DeviceParameterMap = {};

        deviceParameterTypes.forEach(kvp => {
            newDeviceParameterMap[kvp.name] = this.deviceParameterDefaults[+kvp.value];
        });

        return newDeviceParameterMap;
    }

    uniqueDeviceProfileNameValidator(deviceProfile: IDeviceParameterProfile, deviceProfiles: IDeviceParameterProfile[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!deviceProfiles?.length || !control.value) {
                return null;
            }

            const isDuplicate = this.deviceProfileNameInUse(deviceProfile, deviceProfiles);

            return isDuplicate ? { customMessage: this.translationService.t("DeviceProfileEditor.UniqueStyleNameWarning") } : null;
        };
    }

    deviceProfileNameInUse(deviceProfile: IDeviceParameterProfile, deviceProfiles: IDeviceParameterProfile[]): boolean {
        if (!deviceProfile?.Name || !deviceProfiles?.length) {
            return false;
        }

        const matchingStyles = deviceProfiles.filter(existingDrivingStyle => {

            return existingDrivingStyle.Name.toLowerCase() === deviceProfile.Name.toLowerCase()
                && existingDrivingStyle.Id !== deviceProfile.Id;
        });

        return matchingStyles?.length > 0;
    }

    getParameterLabelForProfile(deviceProfile: IDeviceParameterProfile): string {
        let parameterLabel = "";

        if (!deviceProfile?.ParameterMap) {
            return parameterLabel;
        }

        const parameterKeys: string[] = Object.keys(deviceProfile?.ParameterMap);

        if (!parameterKeys?.length) {
            return parameterLabel;
        }

        return `${parameterKeys.length}`;
    }

    createMappingPillsForDeviceProfile(deviceProfile: IDeviceParameterProfile): void {

        if (!deviceProfile) {
            return;
        }

        deviceProfile.appliesToLabel = "";

        if (!deviceProfile.UnitDeviceParameterProfileMappings?.length) {
            return;
        }

        if (this.hasAllResourcesMapping(deviceProfile)) {
            deviceProfile.appliesToLabel = this.translationService.t("DeviceProfileEditor.AllResourcesMapping");

            return;
        }

        deviceProfile.appliesToLabel = this.translationService.t("DeviceProfileEditor.TotalVehicleMappings", deviceProfile.UnitDeviceParameterProfileMappings.length);
    }

    hasAllResourcesMapping(deviceProfile: IDeviceParameterProfile): boolean {
        if (!deviceProfile.UnitDeviceParameterProfileMappings?.length) {
            false;
        }

        return deviceProfile.UnitDeviceParameterProfileMappings.length === 1
            && !deviceProfile.UnitDeviceParameterProfileMappings[0].VehicleId;
    }

    hasVehicleIdMapping(deviceProfile: IDeviceParameterProfile, vehicleId: number): boolean {
        if (!deviceProfile.UnitDeviceParameterProfileMappings?.length) {
            false;
        }

        return !!deviceProfile.UnitDeviceParameterProfileMappings.find(mapping => mapping.VehicleId === vehicleId);
    }

    mapDeviceProfilesToVehicles(deviceProfiles: IDeviceParameterProfile[], vehicles: IVehicle[]): void {

        if (!deviceProfiles?.length || !vehicles?.length) {
            console.warn("Can't map device profiles due to missing resources");
            return;
        }

        const allResourcesProfile = deviceProfiles.find(profile => this.hasAllResourcesMapping(profile));

        vehicles.forEach(vehicle => {
            vehicle.deviceProfile = allResourcesProfile;

            const matchingProfile = deviceProfiles.find(profile => this.hasVehicleIdMapping(profile, vehicle.Id));

            if (matchingProfile) {
                vehicle.deviceProfile = matchingProfile;
            }
        });
    }

    mapDeviceEventTimeToVehicles(latestDeviceParameterRecords: IDeviceParameterRecord[], unitMappings: IVehicleUnitMapping[], vehicles: IVehicle[]) {
        vehicles.forEach(vehicle => {
            if (!vehicle.PlotId) {
                return;
            }

            const unitMapping = unitMappings.find(mapping => mapping.VehicleId == vehicle.Id);

            if (!unitMapping) {
                return;
            }

            const latestRecord = latestDeviceParameterRecords.find(record => record.Serial == unitMapping.Serial);

            if (latestRecord) {
                vehicle.deviceLastSynced = latestRecord.EventTime;
            }
        })
    }

    mapDeviceParameterComparisonResultsToVehicles(deviceParameterComparisonResultMap: DeviceParameterComparisonResultMap, vehicles: IVehicle[]): void {
        if (!Object.keys(deviceParameterComparisonResultMap)?.length || !vehicles?.length) {
            console.warn("Can't map device profiles due to missing resources");
            return;
        }

        vehicles.forEach(vehicle => {
            if (!deviceParameterComparisonResultMap[vehicle.Id]) {
                vehicle.deviceParameterComparisonResult = {
                    Comparisons: [],
                    IsCorrect: false,
                    vehicleHasUnit: vehicle.HasUnit,
                    hasAssignedProfile: !!vehicle.deviceProfile
                }

                return;
            }

            vehicle.deviceParameterComparisonResult = deviceParameterComparisonResultMap[vehicle.Id];
            vehicle.deviceParameterComparisonResult.vehicleHasUnit = vehicle.HasUnit;
            vehicle.deviceParameterComparisonResult.hasAssignedProfile = !!vehicle.deviceProfile;
        });
    }

    getDeviceParameterComparisonResultDisplayStatus(result: IDeviceParameterComparisonResult, translate: boolean = false): string {
        const status: DeviceComparisonStatus = this.getDeviceParameterComparisonResultStatus(result);

        return (translate)
            ? this.translationService.tEnum(DeviceComparisonStatus, "DeviceComparisonStatus", status)
            : DeviceComparisonStatus[status];
    }

    getDeviceParameterComparisonResultStatus(result: IDeviceParameterComparisonResult): DeviceComparisonStatus { 
        if (result && !result.vehicleHasUnit) {
            return DeviceComparisonStatus.NoUnitInstalled;
        }

        if (!result || (!result?.Comparisons?.length && result?.vehicleHasUnit)) {
            return DeviceComparisonStatus.IncompatibleUnit;
        }

        if (!result.IsCorrect && !result.hasAssignedProfile) {
            return DeviceComparisonStatus.NoProfileAssigned
        }

        if (!result.IsCorrect) {
            return DeviceComparisonStatus.AwaitingSync;
        }

        return DeviceComparisonStatus.Synchronized;
    }

    getDeviceParameterComparisonResultStatusLabel(result: IDeviceParameterComparisonResult): string {
        const status = this.getDeviceParameterComparisonResultStatus(result);

        switch (status) {
            case DeviceComparisonStatus.NoUnitInstalled: return this.translationService.t("DeviceProfileService.NoUnitInstalledLabel");
            case DeviceComparisonStatus.IncompatibleUnit: return this.translationService.t("DeviceProfileService.IncompatibleUnitLabel");
            case DeviceComparisonStatus.AwaitingSync: return this.translationService.t("DeviceProfileService.AwaitingSyncLabel");
            case DeviceComparisonStatus.Synchronized: return this.translationService.t("DeviceProfileService.SynchronizedLabel");
            default: return undefined;
        }
    }

    getDescriptiveDeviceParameterComparisonResultLabel(result: IDeviceParameterComparisonResult): string {

        if (!result) {
            return "";
        }

        if (!result?.Comparisons?.length) {
            return "";
        }

        let totalAwaitingSync = 0;

        result.Comparisons.forEach(comparison => {
            if (!comparison.IsCorrect) {
                totalAwaitingSync++;
            }
        });

        const outOfSyncLabel = (totalAwaitingSync === result.Comparisons.length)
            ? this.translationService.t("DeviceProfileService.AllParametersOutOfSyncLabel")
            : this.translationService.t("DeviceProfileService.XParametersOutOfSync", [result.Comparisons.length - totalAwaitingSync, result.Comparisons.length]);

        const inSyncLabel = ``;

        return (totalAwaitingSync > 0) ? outOfSyncLabel : inSyncLabel;
    }
}
